import { useMemo } from 'react';

import { OrderStatusType } from 'api/types/entity/account/order/orderStatusType';
import { useTranslation } from 'libs/i18n';
import { Chip, ChipProps } from 'libs/ui';

export interface ChipOrderStatusProps
  extends Pick<ChipProps, 'variant' | 'uppercase'> {
  status: OrderStatusType;
}

export const ChipOrderStatus = ({
  status,
  variant,
  uppercase,
}: ChipOrderStatusProps) => {
  const { t } = useTranslation();

  const color = useMemo(() => {
    switch (status) {
      case OrderStatusType.AwaitingSigning:
        return 'blue50';
      case OrderStatusType.Signed:
      case OrderStatusType.New:
      case OrderStatusType.AwaitingComplianceCheck:
      case OrderStatusType.InComplianceCheck:
      case OrderStatusType.ComplianceCheckPassed:
      case OrderStatusType.AwaitingAfmSubmission:
      case OrderStatusType.SentToAfm:
      case OrderStatusType.InProgress:
      case OrderStatusType.AwaitingExecutionCheck:
      case OrderStatusType.ExecutionCheck:
        return 'yellow';

      case OrderStatusType.Executed:
        return 'green';
      case OrderStatusType.PartiallyExecuted:
        return 'blue100';
      case OrderStatusType.NotExecuted:
        return 'red';
      case OrderStatusType.Revoked:
        return 'purple50';
      case OrderStatusType.Cancelled:
        return 'purple150';
      default:
        return 'blue50';
    }
  }, [status]);

  const text = useMemo(() => {
    switch (status) {
      case OrderStatusType.AwaitingSigning:
        return t('account.order.groupingStatus.awaitingSigning');
      case OrderStatusType.Signed:
      case OrderStatusType.New:
      case OrderStatusType.AwaitingComplianceCheck:
      case OrderStatusType.InComplianceCheck:
      case OrderStatusType.ComplianceCheckPassed:
      case OrderStatusType.AwaitingAfmSubmission:
      case OrderStatusType.SentToAfm:
      case OrderStatusType.InProgress:
      case OrderStatusType.AwaitingExecutionCheck:
      case OrderStatusType.ExecutionCheck:
        return t('account.order.groupingStatus.inProgress');
      case OrderStatusType.Executed:
        return t('account.order.groupingStatus.executed');
      case OrderStatusType.PartiallyExecuted:
        return t('account.order.groupingStatus.partiallyExecuted');
      case OrderStatusType.NotExecuted:
        return t('account.order.groupingStatus.notExecuted');
      case OrderStatusType.Revoked:
        return t('account.order.groupingStatus.revoked');
      case OrderStatusType.Cancelled:
        return t('account.order.groupingStatus.cancelled');
      default:
        return t('account.order.status.AwaitingSigning');
    }
  }, [status, t]);

  return (
    <Chip color={color} text={text} uppercase={uppercase} variant={variant} />
  );
};
